import { clsx } from 'clsx'

import { InfoIcon } from '@buffer-mono/popcorn'

import { SERVICE_YOUTUBE } from '~publish/legacy/constants'
import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import { parseYoutubeLinks } from '~publish/legacy/utils/channels/youtube/helpers'

import {
  LikeIcon,
  DislikeIcon,
  CommentIcon,
  ShareIcon,
} from '../../../../assets/youtube'
import {
  EmptyMediaPlaceholder,
  PostWrapper,
  PreviewAiMarker,
} from '../../../Common/styles'
import {
  DEFAULT_AVATAR,
  getOrientation,
  getPreviewTitleCharLimit,
  ImageOrientation,
  textExceedsCharLimit,
  truncateText,
} from '../../../Common/utils'
import { VideoPlayer as VideoPlayerCommon } from '../../../Common/VideoPlayer'
import type { ProfilePreview, YoutubeContent } from '../../../types'

import styles from './Post.module.css'

import type { JSX } from 'react'

const maxLengthTitle = getPreviewTitleCharLimit(SERVICE_YOUTUBE)
const maxLengthUsername = 17

export const YoutubePost = ({
  content,
  profile,
}: {
  content: YoutubeContent
  profile: ProfilePreview
}): JSX.Element => {
  const { avatar, username } = profile
  const { video, channelData, isAiGenerated } = content
  const youtube = channelData?.youtube

  const videoHeight = video?.height
  const videoWidth = video?.width
  const orientation = getOrientation({
    height: videoHeight,
    width: videoWidth,
  })

  const titleToDisplay = truncateText(youtube?.title, maxLengthTitle)
  const usernameToDisplay = truncateText(username, maxLengthUsername)

  return (
    <PostWrapper>
      {video && (
        <VideoPlayerCommon
          video={video}
          className={clsx(
            styles.videoPlayer,
            orientation === ImageOrientation.Square && styles.square,
            orientation === ImageOrientation.Portrait && styles.portrait,
          )}
        />
      )}
      {!video && (
        <EmptyMediaPlaceholder service="youtube" className={styles.empty} />
      )}
      <div className={styles.metadataWrapper}>
        <div className={styles.userDataWrapper}>
          <div
            className={styles.avatar}
            style={{
              backgroundImage: `url(${avatar}), url(${DEFAULT_AVATAR})`,
            }}
          />
          <span
            className={styles.username}
            data-testid="username"
            translate="no"
          >
            @{usernameToDisplay}
            {username &&
              textExceedsCharLimit(username, maxLengthUsername) &&
              '...'}
          </span>
          <div className={styles.subscribeButton}>Subscribe</div>
        </div>
        <LinkifiedText
          className={styles.linkifiedText}
          links={parseYoutubeLinks(titleToDisplay)}
          size="small"
          newTab
        >
          {titleToDisplay}
        </LinkifiedText>
        {isAiGenerated && (
          <PreviewAiMarker
            icon={<InfoIcon />}
            label="AI"
            className={styles.aiMarker}
          />
        )}
      </div>
      <div
        className={clsx(
          styles.iconsSideSection,
          orientation === ImageOrientation.Square &&
            styles.iconsSideSectionSquare,
        )}
      >
        <div className={styles.iconWrapper}>
          <LikeIcon size={24} color="white" />
          <span className={styles.iconLabel}>Like</span>
        </div>
        <div className={styles.iconWrapper}>
          <DislikeIcon size={24} color="white" />
          <span className={styles.iconLabel}>Dislike</span>
        </div>
        <div className={styles.iconWrapper}>
          <CommentIcon size={24} color="white" />
          <span className={styles.iconLabel}>Comment</span>
        </div>
        <div className={styles.iconWrapper}>
          <ShareIcon size={24} color="white" />
          <span className={styles.iconLabel}>Share</span>
        </div>
        <div
          className={styles.audioAvatar}
          style={{ backgroundImage: `url(${avatar}), url(${DEFAULT_AVATAR})` }}
        />
      </div>
    </PostWrapper>
  )
}
